import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle, ArrowRight, BellRing, Building2, Check, CheckCircle2, ChevronDown,
  CreditCard, FileText, Gauge, Headphones, Info, MessageSquareText, Receipt,
  ShieldCheck, Sparkles, Users, X, Zap,
} from 'lucide-react';
import { fetchApi } from '../api';
import { useDialogAccessibility } from '../hooks/useDialogAccessibility';
import { getPaymentCheckoutUrl } from '../utils/paymentCheckout';
import '../styles/billing.css';

/**
 * Billing screen.
 *
 * Three things it no longer does:
 *
 * 1. It no longer promises automatic renewal. The old copy read "Ежемесячное
 *    продление можно отключить в любой момент" while nothing in the system could
 *    charge a customer twice: FreeKassa's SCI checkout has no recurring debit,
 *    and the renewal worker's candidate query could never match a row. Clientix
 *    sells prepaid terms and says so.
 *
 * 2. It no longer carries the price list or the promo codes. `PLANS` with its
 *    prices, and the literals `CLIENTIX2026` / `VIP20`, all shipped inside the
 *    browser bundle — so the largest discount was public. Prices come from
 *    GET /billing/pricing and codes are validated server-side.
 *
 * 3. It no longer shows a refund button that always failed. That button called a
 *    provider method hardcoded to `success: false`, which reached the customer as
 *    "Internal Server Error" and the owner as a Sentry incident, on every click.
 *    A refund is now a request that a person receives.
 */

interface PricingPageProps { user?: { plan?: string }; onPlanUpdated?: (newPlan: string) => void }

type PlanTier = 'STARTER' | 'PRO' | 'BUSINESS' | 'ENTERPRISE';

type PriceBreakdown = {
  tier: PlanTier;
  termMonths: number;
  monthlyPrice: number;
  baseAmount: number;
  termDiscountPercent: number;
  termDiscountAmount: number;
  subtotal: number;
  promoDiscountPercent: number;
  promoDiscountAmount: number;
  total: number;
  effectiveMonthlyPrice: number;
  savingsVersusMonthly: number;
};

type CatalogPlan = {
  id: PlanTier;
  name: string;
  monthlyPrice: number;
  purchasable: boolean;
  limits: {
    aiMessagesPerMonth: number; seats: number; channels: number; products: number;
    knowledgeBase: boolean; advancedRag: boolean; addons: boolean;
    seatsUnlimited: boolean; channelsUnlimited: boolean; productsUnlimited: boolean;
  };
  prices: PriceBreakdown[];
};

type TermOption = { months: number; discountPercent: number; label: string; badge?: string };

type PricingCatalog = {
  plans: CatalogPlan[];
  terms: TermOption[];
  defaultTermMonths: number;
  recurring: boolean;
};

type Subscription = {
  plan?: PlanTier;
  effectivePlan?: PlanTier;
  effectivePlanName?: string;
  planName?: string;
  state?: 'active' | 'expiring' | 'expired' | 'canceled';
  daysLeft?: number;
  downgraded?: boolean;
  paid?: boolean;
  termMonths?: number;
  aiMessagesUsed?: number;
  aiMessagesLimit?: number;
  aiMessagesLeft?: number;
  addonMessages?: number;
  currentPeriodEnd?: string;
  remindersOptOut?: boolean;
};

type RefundRequest = { id: number; status: string; amount: number };

type Payment = {
  id: number; paymentId: string; amount: number; status: string; kind?: string;
  tier: string; termMonths?: number; createdAt: string;
  invoiceUrl?: string | null; refundRequests?: RefundRequest[];
};

/** Copy that describes what each plan is for. Marketing text, not pricing data —
 *  the numbers all arrive from the API. */
const PLAN_COPY: Record<PlanTier, { eyebrow: string; description: string; features: string[] }> = {
  STARTER: {
    eyebrow: 'Попробовать продукт',
    description: 'Для первых живых диалогов и проверки Clientix на своей команде.',
    features: ['Единое окно диалогов', 'Воронка продаж и каталог', 'База знаний агента', 'Базовая аналитика'],
  },
  PRO: {
    eyebrow: 'Основной тариф',
    description: 'Для отдела, который хочет закрывать больше обращений без роста штата.',
    features: ['ИИ-автопилот 24/7', 'Квалификация лидов и автодействия', 'Коммерческие предложения', 'Неограниченные диалоги и товары', 'Приоритетная поддержка'],
  },
  BUSINESS: {
    eyebrow: 'Высокая нагрузка',
    description: 'Для нескольких команд, большой базы знаний и потока из всех каналов.',
    features: ['Всё из тарифа «Рост»', 'Семантический поиск по документам', 'Расширенная аналитика', 'Интеграции с CRM и 1С', 'Персональный менеджер'],
  },
  ENTERPRISE: {
    eyebrow: 'Свой контур',
    description: 'On-premise, SLA и сопровождение инженера.',
    features: ['Выделенный контур', 'Кастомные интеграции', 'SLA и сопровождение'],
  },
};

const FAQ = [
  {
    q: 'Есть ли автоматическое продление?',
    a: 'Нет. Clientix не хранит платёжные данные и не может списать деньги повторно — оплата всегда происходит по вашей инициативе. Мы напомним об окончании оплаченного периода за 7, 3 и 1 день и в день окончания.',
  },
  {
    q: 'Что будет, когда оплаченный период закончится?',
    a: 'Аккаунт перейдёт на бесплатный тариф «Старт»: 1 канал, 1 место, 100 ИИ-ответов в месяц. Диалоги, сделки, товары и настройки сохраняются полностью — вернуть прежние лимиты можно оплатой в любой момент.',
  },
  {
    q: 'Почему скидка растёт с числом месяцев?',
    a: 'Чем длиннее период, тем меньше наши издержки на приём платежей и поддержку. Скидка фиксированная и видна до оплаты: 3 месяца — 5%, 6 месяцев — 12%, 12 месяцев — 20%.',
  },
  {
    q: 'Что именно считается ИИ-ответом?',
    a: 'Один отправленный клиенту ответ, созданный ИИ-агентом. Сообщения менеджеров и входящие обращения лимит не расходуют.',
  },
  {
    q: 'Можно ли продлить период досрочно?',
    a: 'Да. Новый срок прибавляется к остатку текущего — оплаченные дни не сгорают.',
  },
  {
    q: 'Как оформить возврат?',
    a: 'Оставьте заявку рядом с платежом в истории. Возврат по правилам платёжной системы проводит человек, поэтому мы отвечаем на email в течение трёх рабочих дней.',
  },
];

const money = (value: number) => `${Math.round(value).toLocaleString('ru-RU')} ₽`;
const monthsWord = (count: number) => {
  const mod100 = count % 100;
  const mod10 = count % 10;
  if (mod100 >= 11 && mod100 <= 14) return 'месяцев';
  if (mod10 === 1) return 'месяц';
  if (mod10 >= 2 && mod10 <= 4) return 'месяца';
  return 'месяцев';
};
const daysWord = (count: number) => {
  const mod100 = count % 100;
  const mod10 = count % 10;
  if (mod100 >= 11 && mod100 <= 14) return 'дней';
  if (mod10 === 1) return 'день';
  if (mod10 >= 2 && mod10 <= 4) return 'дня';
  return 'дней';
};
const limitLabel = (value: number, unlimited: boolean) => (unlimited ? 'без ограничений' : value.toLocaleString('ru-RU'));

export default function PricingPage({ user, onPlanUpdated }: PricingPageProps) {
  useDialogAccessibility();

  const [catalog, setCatalog] = useState<PricingCatalog | null>(null);
  const [catalogError, setCatalogError] = useState('');
  const [termMonths, setTermMonths] = useState(3);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [history, setHistory] = useState<Payment[]>([]);
  const [selectedPlan, setSelectedPlan] = useState<CatalogPlan | null>(null);
  const [paymentMethod, setPaymentMethod] = useState<'sbp' | 'invoice'>('sbp');
  const [promoCode, setPromoCode] = useState('');
  const [promoState, setPromoState] = useState<{ applied: boolean; message: string; price?: PriceBreakdown }>({ applied: false, message: '' });
  const [checkingPromo, setCheckingPromo] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [notice, setNotice] = useState('');
  const [volume, setVolume] = useState(2500);
  const [openFaq, setOpenFaq] = useState(0);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [refundTarget, setRefundTarget] = useState<Payment | null>(null);
  const [refundReason, setRefundReason] = useState('');
  const [refundSending, setRefundSending] = useState(false);

  const loadBilling = useCallback(async () => {
    try {
      const [sub, payments] = await Promise.all([
        fetchApi('/billing/subscription', { cache: 'no-store' }),
        fetchApi('/billing/history', { cache: 'no-store' }).catch(() => []),
      ]);
      if (sub && !sub.error) setSubscription(sub);
      if (Array.isArray(payments)) setHistory(payments);
    } catch (error) {
      console.error('[PricingPage] Failed to load billing:', error);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    let confirmationTimer: number | undefined;
    (async () => {
      try {
        const data = await fetchApi('/billing/pricing');
        if (cancelled) return;
        setCatalog(data);
        setTermMonths(data?.defaultTermMonths ?? 3);
      } catch (error) {
        if (!cancelled) {
          setCatalogError(error instanceof Error ? error.message : 'Не удалось загрузить тарифы');
        }
      }
    })();
    void loadBilling();

    const status = new URLSearchParams(window.location.search).get('status');
    if (status === 'success') {
      setNotice('Платёж принят. Тариф активируется после подтверждения платёжной системой.');

      let refreshCount = 0;
      confirmationTimer = window.setInterval(() => {
        refreshCount += 1;
        void loadBilling();

        if (refreshCount >= 15 && confirmationTimer !== undefined) {
          window.clearInterval(confirmationTimer);
          confirmationTimer = undefined;
        }
      }, 2000);
    }
    if (status === 'cancel') setNotice('Оплата не завершена. Вы можете повторить её в любое время.');
    if (status) window.history.replaceState({}, document.title, `${window.location.pathname}#billing`);

    return () => {
      cancelled = true;
      if (confirmationTimer !== undefined) window.clearInterval(confirmationTimer);
    };
  }, [loadBilling]);

  const currentPlan = String(subscription?.effectivePlan || subscription?.plan || user?.plan || 'STARTER').toUpperCase() as PlanTier;
  const used = subscription?.aiMessagesUsed ?? 0;
  const limit = subscription?.aiMessagesLimit ?? 100;
  const usage = Math.min(100, Math.round((used / Math.max(1, limit)) * 100));
  const state = subscription?.state ?? 'active';

  const plans = useMemo(() => catalog?.plans.filter((plan) => plan.id !== 'ENTERPRISE') ?? [], [catalog]);
  const terms = catalog?.terms ?? [];

  const priceFor = useCallback(
    (plan: CatalogPlan, months = termMonths) => plan.prices.find((price) => price.termMonths === months) ?? plan.prices[0],
    [termMonths],
  );

  const recommended = useMemo(() => {
    if (plans.length === 0) return null;
    return plans.find((plan) => plan.limits.aiMessagesPerMonth >= volume) ?? plans[plans.length - 1];
  }, [plans, volume]);

  /** The authoritative figure is whatever the server last quoted. */
  const checkoutPrice = useMemo(() => {
    if (!selectedPlan) return null;
    if (promoState.applied && promoState.price && promoState.price.termMonths === termMonths) return promoState.price;
    return priceFor(selectedPlan);
  }, [promoState, priceFor, selectedPlan, termMonths]);

  const openCheckout = (plan: CatalogPlan) => {
    if (!plan.purchasable) return;
    setSelectedPlan(plan);
    setPaymentMethod('sbp');
    setPromoCode('');
    setPromoState({ applied: false, message: '' });
  };

  const closeCheckout = () => {
    setSelectedPlan(null);
    setPromoState({ applied: false, message: '' });
  };

  const applyPromo = async () => {
    if (!selectedPlan) return;
    const code = promoCode.trim();
    if (!code) {
      setPromoState({ applied: false, message: 'Введите промокод' });
      return;
    }
    setCheckingPromo(true);
    try {
      // Validated on the server. The old page compared the input against two
      // string literals that therefore shipped to every browser.
      const result = await fetchApi('/billing/promo/preview', {
        method: 'POST',
        body: JSON.stringify({ code, tier: selectedPlan.id, termMonths }),
      });
      setPromoState({
        applied: Boolean(result?.applied),
        message: result?.applied
          ? `Промокод применён: скидка ${result.price.promoDiscountPercent}%`
          : 'Промокод не применён',
        price: result?.price,
      });
    } catch (error) {
      setPromoState({ applied: false, message: error instanceof Error ? error.message : 'Промокод не найден' });
    } finally {
      setCheckingPromo(false);
    }
  };

  const pay = async () => {
    if (!selectedPlan) return;
    setProcessing(true);
    try {
      const result = await fetchApi('/billing/checkout', {
        method: 'POST',
        body: JSON.stringify({
          tier: selectedPlan.id,
          termMonths,
          paymentMethod,
          promoCode: promoState.applied ? promoCode.trim() : undefined,
        }),
      });

      if (result?.invoice?.url) {
        setNotice(`Счёт № ${result.invoice.number} сформирован. Доступ откроется после поступления оплаты.`);
        closeCheckout();
        await loadBilling();
        window.open(result.invoice.url, '_blank', 'noopener,noreferrer');
        return;
      }

      const url = getPaymentCheckoutUrl(result);
      if (url) { window.location.assign(url); return; }

      if (result?.status === 'succeeded') {
        const { id, name } = selectedPlan;
        await loadBilling();
        onPlanUpdated?.(id);
        closeCheckout();
        setNotice(`Тариф «${name}» активирован на ${termMonths} ${monthsWord(termMonths)}.`);
      } else {
        setNotice(result?.error || 'Не удалось создать платёж. Попробуйте ещё раз.');
      }
    } catch (error) {
      setNotice(error instanceof Error ? error.message : 'Не удалось создать платёж.');
    } finally {
      setProcessing(false);
    }
  };

  const buyAddon = async (packageType: '1000' | '5000') => {
    try {
      const result = await fetchApi('/billing/addon', { method: 'POST', body: JSON.stringify({ packageType }) });
      const url = getPaymentCheckoutUrl(result);
      if (url) window.location.assign(url);
      else setNotice(result?.error || 'Не удалось создать платёж за пакет.');
    } catch (error) {
      setNotice(error instanceof Error ? error.message : 'Не удалось купить пакет.');
    }
  };

  const submitRefundRequest = async () => {
    if (!refundTarget) return;
    setRefundSending(true);
    try {
      const result = await fetchApi('/billing/refund-request', {
        method: 'POST',
        body: JSON.stringify({ paymentId: refundTarget.paymentId, reason: refundReason.trim() || undefined }),
      });
      setNotice(result?.message || 'Заявка на возврат принята.');
      setRefundTarget(null);
      setRefundReason('');
      await loadBilling();
    } catch (error) {
      setNotice(error instanceof Error ? error.message : 'Не удалось отправить заявку.');
    } finally {
      setRefundSending(false);
    }
  };

  const toggleReminders = async (enabled: boolean) => {
    try {
      const result = await fetchApi('/billing/renewal-reminders', {
        method: 'POST',
        body: JSON.stringify({ remindersEnabled: enabled }),
      });
      setSubscription((previous) => (previous ? { ...previous, remindersOptOut: !enabled } : previous));
      setNotice(result?.message || '');
    } catch (error) {
      setNotice(error instanceof Error ? error.message : 'Не удалось изменить настройку напоминаний.');
    }
  };

  return (
    <main className="pricing-page cx-pricing">
      <div className="cx-pricing-shell">
        {notice && (
          <div className="cx-notice" role="status">
            <CheckCircle2 size={18} /><span>{notice}</span>
            <button type="button" onClick={() => setNotice('')} aria-label="Закрыть"><X size={16} /></button>
          </div>
        )}

        {state === 'expired' && (
          <div className="cx-sub-state expired" role="alert">
            <AlertTriangle size={19} aria-hidden="true" />
            <div>
              <strong>Оплаченный период закончился</strong>
              <p>
                {subscription?.planName ? `Тариф «${subscription.planName}» ` : 'Платный тариф '}
                больше не действует, аккаунт работает на бесплатном «Старте». Диалоги, сделки и настройки сохранены —
                оплатите период, чтобы вернуть каналы, места и ИИ-ответы.
              </p>
              {recommended && <button type="button" onClick={() => openCheckout(recommended)}>Продлить тариф</button>}
            </div>
          </div>
        )}

        {state === 'expiring' && (
          <div className="cx-sub-state expiring" role="status">
            <BellRing size={19} aria-hidden="true" />
            <div>
              <strong>
                Оплаченный период заканчивается
                {typeof subscription?.daysLeft === 'number'
                  ? subscription.daysLeft > 0
                    ? ` через ${subscription.daysLeft} ${daysWord(subscription.daysLeft)}`
                    : ' сегодня'
                  : ''}
              </strong>
              <p>
                Автоматического списания нет. Продлите период вручную, чтобы не потерять лимиты
                {subscription?.currentPeriodEnd ? ` после ${new Date(subscription.currentPeriodEnd).toLocaleDateString('ru-RU')}` : ''}.
              </p>
              {plans.find((plan) => plan.id === subscription?.plan) && (
                <button type="button" onClick={() => openCheckout(plans.find((plan) => plan.id === subscription?.plan)!)}>
                  Продлить сейчас
                </button>
              )}
            </div>
          </div>
        )}

        <section className="cx-pricing-hero">
          <div className="cx-hero-copy">
            <span className="cx-kicker"><Sparkles size={14} /> Платите за срок, который вам нужен</span>
            <h1>Одна платформа.<br /><em>Понятный масштаб.</em></h1>
            <p>CRM, каналы и ИИ-агент уже внутри. Выберите объём и срок — чем длиннее период, тем ниже цена месяца.</p>
            <div className="cx-trust-row">
              <span><Check size={14} /> Без платы за внедрение</span>
              <span><Check size={14} /> Без автосписаний</span>
              <span><Check size={14} /> Данные остаются вашими</span>
            </div>
          </div>
          <aside className="cx-current-plan" aria-label="Текущая подписка">
            <div className="cx-current-top">
              <div>
                <span>Текущий тариф</span>
                <strong>{subscription?.effectivePlanName || currentPlan}</strong>
              </div>
              <span className="cx-live-dot">
                {state === 'expired' ? 'Истёк' : state === 'expiring' ? 'Заканчивается' : 'Активен'}
              </span>
            </div>
            <div className="cx-usage-number">
              <strong>{used.toLocaleString('ru-RU')}</strong>
              <span>из {limit.toLocaleString('ru-RU')} ИИ-ответов</span>
            </div>
            <div className="cx-usage-track"><i style={{ width: `${usage}%` }} /></div>
            <div className="cx-current-bottom">
              <span>{usage}% использовано</span>
              <span>
                {subscription?.currentPeriodEnd
                  ? `${state === 'expired' ? 'истёк' : 'до'} ${new Date(subscription.currentPeriodEnd).toLocaleDateString('ru-RU')}`
                  : 'текущий период'}
              </span>
            </div>
          </aside>
        </section>

        <section className="cx-plan-section" aria-labelledby="plans-title">
          <div className="cx-section-heading">
            <div>
              <span className="cx-section-index">01 / Тарифы</span>
              <h2 id="plans-title">Начните бесплатно. Платите за нужный срок.</h2>
            </div>
            <div className="cx-term-switch" role="group" aria-label="Срок оплаты">
              {terms.map((term) => (
                <button
                  key={term.months}
                  type="button"
                  className={term.months === termMonths ? 'active' : ''}
                  onClick={() => setTermMonths(term.months)}
                  aria-pressed={term.months === termMonths}
                >
                  <strong>{term.label}</strong>
                  <span>{term.badge || 'базовая цена'}</span>
                </button>
              ))}
            </div>
          </div>

          {catalogError && (
            <p className="cx-promo-error" role="alert">Тарифы недоступны: {catalogError}</p>
          )}

          <div className="cx-plan-grid">
            {plans.map((plan) => {
              const isCurrent = plan.id === currentPlan && state !== 'expired';
              const price = priceFor(plan);
              const copy = PLAN_COPY[plan.id];
              return (
                <article key={plan.id} className={`cx-plan-card ${plan.id === 'PRO' ? 'featured' : ''} ${isCurrent ? 'current' : ''}`}>
                  {plan.id === 'PRO' && <span className="cx-popular-label">Выбор большинства</span>}
                  <header>
                    <span>{copy.eyebrow}</span>
                    <h3>{plan.name}</h3>
                    <p>{copy.description}</p>
                  </header>
                  <div className="cx-price">
                    <strong>{price?.effectiveMonthlyPrice ? money(price.effectiveMonthlyPrice) : '0 ₽'}</strong>
                    <span>/ месяц</span>
                  </div>
                  {price && price.total > 0 && (
                    <p className="cx-year-total">
                      {money(price.total)} за {price.termMonths} {monthsWord(price.termMonths)}
                      {price.savingsVersusMonthly > 0 && ` · экономия ${money(price.savingsVersusMonthly)}`}
                    </p>
                  )}
                  <button
                    type="button"
                    className="cx-plan-button"
                    disabled={!plan.purchasable || (isCurrent && state === 'active')}
                    onClick={() => openCheckout(plan)}
                  >
                    {!plan.purchasable
                      ? 'Доступен сразу'
                      : isCurrent
                        ? state === 'active' ? 'Ваш текущий тариф' : <>Продлить «{plan.name}» <ArrowRight size={16} /></>
                        : <>Выбрать «{plan.name}» <ArrowRight size={16} /></>}
                  </button>
                  <div className="cx-plan-metrics">
                    <span><MessageSquareText size={16} /><b>{plan.limits.aiMessagesPerMonth.toLocaleString('ru-RU')}</b> ИИ-ответов/мес</span>
                    <span><Users size={16} /><b>{limitLabel(plan.limits.seats, plan.limits.seatsUnlimited)}</b> мест</span>
                    <span><Gauge size={16} /><b>{limitLabel(plan.limits.channels, plan.limits.channelsUnlimited)}</b> каналов</span>
                  </div>
                  <ul>{copy.features.map((feature) => <li key={feature}><Check size={15} /> {feature}</li>)}</ul>
                </article>
              );
            })}
          </div>

          <div className="cx-billing-truth">
            <Info size={18} aria-hidden="true" />
            <div>
              <strong>Автопродления нет — и это осознанно</strong>
              <p>
                Clientix не хранит данные вашей карты и не может списать деньги повторно: наша платёжная система
                не поддерживает регулярные списания. Вместо этого вы покупаете срок целиком, а мы напоминаем об
                оплате за 7, 3 и 1 день и в день окончания периода. Оплаченные дни не сгорают — при досрочном
                продлении новый срок прибавляется к остатку.
              </p>
            </div>
          </div>

          <div className="cx-enterprise-strip">
            <div className="cx-enterprise-icon"><Building2 size={24} /></div>
            <div>
              <span>Enterprise</span>
              <strong>Свой контур, SLA и объём от 100 000 ответов</strong>
              <p>On-premise, кастомные интеграции и сопровождение инженера.</p>
            </div>
            <a href="mailto:sales@clientix.ru?subject=Clientix%20Enterprise">Обсудить проект <ArrowRight size={16} /></a>
          </div>
        </section>

        {recommended && (
          <section className="cx-fit-section" aria-labelledby="fit-title">
            <div className="cx-fit-copy">
              <span className="cx-section-index">02 / Подбор</span>
              <h2 id="fit-title">Не уверены в объёме?</h2>
              <p>Укажите, сколько ответов клиентам в месяц вы хотите передать ИИ. Мы покажем минимальный подходящий тариф.</p>
              <label htmlFor="ai-volume">ИИ-ответов в месяц <strong>{volume.toLocaleString('ru-RU')}</strong></label>
              <input
                id="ai-volume" type="range" min="100" max="15000" step="100"
                value={volume} onChange={(event) => setVolume(Number(event.target.value))}
              />
              <div className="cx-range-labels"><span>100</span><span>3 000</span><span>15 000</span></div>
            </div>
            <div className="cx-fit-result">
              <span>Подойдёт тариф</span>
              <strong>{recommended.name}</strong>
              <p>До {recommended.limits.aiMessagesPerMonth.toLocaleString('ru-RU')} ИИ-ответов в месяц</p>
              <div>
                <small>От</small>
                <b>{money(priceFor(recommended)?.effectiveMonthlyPrice ?? recommended.monthlyPrice)}</b>
                <small>/ месяц при оплате за {termMonths} {monthsWord(termMonths)}</small>
              </div>
              {recommended.purchasable && (
                <button type="button" onClick={() => openCheckout(recommended)}>Выбрать тариф <ArrowRight size={16} /></button>
              )}
            </div>
          </section>
        )}

        <section className="cx-addons-section" aria-labelledby="addons-title">
          <div className="cx-section-heading">
            <div><span className="cx-section-index">03 / Гибкость</span><h2 id="addons-title">Пиковая нагрузка — без смены тарифа</h2></div>
            <p>Докупленные ответы не сгорают при продлении тарифа.</p>
          </div>
          <div className="cx-addon-grid">
            <article>
              <div><Zap size={20} /><span><b>+1 000 ответов</b><small>Для короткого всплеска нагрузки</small></span></div>
              <strong>990 ₽</strong>
              <button type="button" disabled={!subscription?.paid} onClick={() => void buyAddon('1000')}>Докупить</button>
            </article>
            <article>
              <div><Sparkles size={20} /><span><b>+5 000 ответов</b><small>На 19% дешевле за ответ</small></span></div>
              <strong>3 990 ₽</strong>
              <button type="button" disabled={!subscription?.paid} onClick={() => void buyAddon('5000')}>Докупить</button>
            </article>
          </div>
          {!subscription?.paid && (
            <p className="cx-addon-hint">
              {state === 'expired'
                ? 'Пакеты доступны, пока действует оплаченный период. Продлите тариф, и пакет можно будет докупить сразу.'
                : 'Дополнительные пакеты доступны на платных тарифах.'}
            </p>
          )}
        </section>

        {subscription?.paid && (
          <div className="cx-reminder-row">
            <div>
              <strong>Напоминать об окончании оплаченного периода</strong>
              <small>
                Письмо и уведомление за 7, 3 и 1 день до конца срока. Если выключить, оплаченный период
                всё равно продолжит действовать до конца — просто без напоминаний.
              </small>
            </div>
            <label>
              <input
                type="checkbox"
                checked={!subscription?.remindersOptOut}
                onChange={(event) => void toggleReminders(event.target.checked)}
              />
              {' '}Напоминать
            </label>
          </div>
        )}

        <section className="cx-assurance-grid" aria-label="Условия оплаты">
          <article><Receipt size={21} /><div><strong>Прозрачная цена</strong><span>Итоговая сумма и скидка видны до перехода к оплате.</span></div></article>
          <article><ShieldCheck size={21} /><div><strong>Безопасный платёж</strong><span>Данные карты обрабатывает платёжный провайдер, Clientix их не хранит.</span></div></article>
          <article><Headphones size={21} /><div><strong>Поможем перейти</strong><span>Поддержка перенесёт каналы и настроит первый сценарий.</span></div></article>
        </section>

        {history.length > 0 && (
          <section className="cx-history-section">
            <button type="button" className="cx-history-toggle" onClick={() => setHistoryOpen((value) => !value)} aria-expanded={historyOpen}>
              <span><Receipt size={18} /> История платежей <small>{history.length}</small></span>
              <ChevronDown size={18} className={historyOpen ? 'open' : ''} />
            </button>
            {historyOpen && (
              <div className="cx-history-list">
                {history.map((payment) => {
                  const pendingRefund = payment.refundRequests?.find((request) => ['pending', 'in_review'].includes(request.status));
                  return (
                    <article key={payment.id}>
                      <div>
                        <b>
                          {payment.kind === 'addon'
                            ? 'Пакет ИИ-ответов'
                            : `Тариф ${payment.tier}${payment.termMonths ? ` · ${payment.termMonths} ${monthsWord(payment.termMonths)}` : ''}`}
                        </b>
                        <small>{new Date(payment.createdAt).toLocaleDateString('ru-RU')} · {payment.paymentId}</small>
                      </div>
                      <strong>{money(payment.amount)}</strong>
                      <span className={`cx-payment-status ${payment.status}`}>
                        {payment.status === 'succeeded' ? 'Оплачен'
                          : payment.status === 'pending' ? 'Ожидает оплаты'
                            : payment.status === 'refunded' ? 'Возвращён'
                              : payment.status === 'partially_refunded' ? 'Частичный возврат'
                                : 'Не оплачен'}
                      </span>
                      <div className="cx-history-actions">
                        {payment.invoiceUrl && (
                          <a href={payment.invoiceUrl} target="_blank" rel="noopener noreferrer">
                            <FileText size={13} /> Счёт
                          </a>
                        )}
                        {pendingRefund
                          ? <span className="cx-refund-pending">Заявка на возврат в обработке</span>
                          : (payment.status === 'succeeded' || payment.status === 'partially_refunded') && (
                            <button type="button" onClick={() => { setRefundTarget(payment); setRefundReason(''); }}>
                              Запросить возврат
                            </button>
                          )}
                      </div>
                    </article>
                  );
                })}
              </div>
            )}
          </section>
        )}

        <section className="cx-faq-section" aria-labelledby="faq-title">
          <span className="cx-section-index">04 / Вопросы</span>
          <h2 id="faq-title">Коротко о тарифах и оплате</h2>
          <div>
            {FAQ.map((item, index) => (
              <article key={item.q}>
                <button type="button" onClick={() => setOpenFaq(openFaq === index ? -1 : index)} aria-expanded={openFaq === index}>
                  <span>{item.q}</span><ChevronDown size={18} />
                </button>
                {openFaq === index && <p>{item.a}</p>}
              </article>
            ))}
          </div>
        </section>
      </div>

      {selectedPlan && checkoutPrice && (
        <div
          className="checkout-modal-overlay cx-checkout-overlay"
          onMouseDown={(event) => event.target === event.currentTarget && closeCheckout()}
        >
          <section className="checkout-modal cx-checkout">
            <button type="button" className="modal-close-btn" onClick={closeCheckout} aria-label="Закрыть"><X size={20} /></button>
            <header>
              <span className="cx-checkout-icon"><CreditCard size={21} /></span>
              <div><span>Оплата тарифа</span><h2>«{selectedPlan.name}»</h2></div>
            </header>

            <div className="cx-term-switch" role="group" aria-label="Срок оплаты">
              {terms.map((term) => (
                <button
                  key={term.months}
                  type="button"
                  className={term.months === termMonths ? 'active' : ''}
                  onClick={() => { setTermMonths(term.months); setPromoState({ applied: false, message: '' }); }}
                  aria-pressed={term.months === termMonths}
                >
                  <strong>{term.label}</strong>
                  <span>{term.badge || 'базовая цена'}</span>
                </button>
              ))}
            </div>

            <div className="cx-checkout-summary">
              <div className="cx-price-lines">
                <div>
                  <span>{checkoutPrice.termMonths} {monthsWord(checkoutPrice.termMonths)} × {money(checkoutPrice.monthlyPrice)}</span>
                  <b>{money(checkoutPrice.baseAmount)}</b>
                </div>
                {checkoutPrice.termDiscountPercent > 0 && (
                  <div className="saving">
                    <span>Скидка за срок −{checkoutPrice.termDiscountPercent}%</span>
                    <b>−{money(checkoutPrice.termDiscountAmount)}</b>
                  </div>
                )}
                {checkoutPrice.promoDiscountPercent > 0 && (
                  <div className="saving">
                    <span>Промокод −{checkoutPrice.promoDiscountPercent}%</span>
                    <b>−{money(checkoutPrice.promoDiscountAmount)}</b>
                  </div>
                )}
                <div className="grand"><span>К оплате</span><strong>{money(checkoutPrice.total)}</strong></div>
              </div>
              <p className="cx-per-month">
                {money(checkoutPrice.effectiveMonthlyPrice)} в месяц.
                {' '}Разовый платёж — автосписаний не будет. Мы напомним об оплате до окончания срока.
              </p>
            </div>

            <div className="cx-promo">
              <input
                value={promoCode}
                onChange={(event) => setPromoCode(event.target.value)}
                placeholder="Промокод"
                aria-label="Промокод"
              />
              <button type="button" onClick={() => void applyPromo()} disabled={checkingPromo}>
                {checkingPromo ? 'Проверяем…' : 'Применить'}
              </button>
            </div>
            {promoState.message && (
              <p className={promoState.applied ? 'cx-promo-ok' : 'cx-promo-error'}>{promoState.message}</p>
            )}

            <div className="cx-method-choice">
              <label>
                <input type="radio" name="cx-method" checked={paymentMethod === 'sbp'} onChange={() => setPaymentMethod('sbp')} />
                <span>Картой или через СБП<small>Мгновенно, доступ откроется после подтверждения платежа</small></span>
              </label>
              <label>
                <input type="radio" name="cx-method" checked={paymentMethod === 'invoice'} onChange={() => setPaymentMethod('invoice')} />
                <span>Счёт для юридического лица<small>Банковский перевод. Доступ откроется после поступления средств</small></span>
              </label>
            </div>

            <button type="button" className="cx-pay-button" disabled={processing} onClick={() => void pay()}>
              {processing
                ? 'Создаём платёж…'
                : paymentMethod === 'invoice'
                  ? <>Сформировать счёт · {money(checkoutPrice.total)} <ArrowRight size={17} /></>
                  : <>Перейти к оплате · {money(checkoutPrice.total)} <ArrowRight size={17} /></>}
            </button>
            <p className="cx-checkout-legal">
              <ShieldCheck size={15} /> Нажимая кнопку, вы принимаете{' '}
              <a href="#legal">условия оферты и политику конфиденциальности</a>.
              Платёжные данные не хранятся в Clientix.
            </p>
          </section>
        </div>
      )}

      {refundTarget && (
        <div
          className="checkout-modal-overlay cx-checkout-overlay"
          onMouseDown={(event) => event.target === event.currentTarget && setRefundTarget(null)}
        >
          <section className="checkout-modal cx-checkout">
            <button type="button" className="modal-close-btn" onClick={() => setRefundTarget(null)} aria-label="Закрыть"><X size={20} /></button>
            <header>
              <span className="cx-checkout-icon"><Receipt size={21} /></span>
              <div><span>Заявка на возврат</span><h2>{money(refundTarget.amount)}</h2></div>
            </header>
            <div className="cx-refund-form">
              <p className="cx-refund-note">
                Возврат по правилам платёжной системы проводит сотрудник поддержки — автоматического возврата
                у нашего платёжного провайдера нет. Мы зарегистрируем заявку и ответим на ваш email
                в течение трёх рабочих дней.
              </p>
              <label htmlFor="refund-reason">Причина возврата (необязательно)</label>
              <textarea
                id="refund-reason"
                value={refundReason}
                onChange={(event) => setRefundReason(event.target.value)}
                maxLength={500}
                placeholder="Например: оплатили не тот тариф"
              />
              <button type="button" className="cx-pay-button" disabled={refundSending} onClick={() => void submitRefundRequest()}>
                {refundSending ? 'Отправляем…' : 'Отправить заявку'}
              </button>
            </div>
          </section>
        </div>
      )}
    </main>
  );
}
